
import React, { useEffect, useRef, useState } from 'react';
import ImageUpload from '../../../sharedcomponents/imageupload/imageupload';
import { fetchApi } from "../../../services/api";
import { buildD3Payload } from '../../../services/payloads/d3Payload';
import { getEightDInfoId } from '../../../services/eightDInfoStore';
import { getCurrentUserEmail } from '../../../services/auth';
import '../d1/d1.css';
import '../d3/d3.css';
import '../d2/d2.css';
import{
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Box,
  Typography,
  IconButton,
  Tooltip,
  Select,
  MenuItem,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Alert,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  Close as CloseIcon,
  Add as AddIcon,
  Remove as RemoveIcon
} from '@mui/icons-material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import TwoMonthDatePicker from '../../../sharedcomponents/two-month-datepicker/TwoMonthDatePicker';

// -----------------------------
// Helpers
// -----------------------------
const toIsoOrNull = (date) => {
  if (!date) return null;
  try {
    const d = date instanceof Date ? date : new Date(date);
    return isNaN(d.getTime()) ? null : d.toISOString();
  } catch {
    return null;
  }
};

const D3 = ({ 
  expanded, 
  onSetClick, 
  handleCloseForm, 
  onToggleAccordion,
  eightDInfoId,
  currentUserEmail = getCurrentUserEmail() || sessionStorage.getItem('userEmail') || '',
  onSaved,
  onResponsibleSet,
  isEnabled = false,
  teamMembers = [],
  d3Data,
  d3ResponsibleData,
  mode,
  readOnly = false,
}) => {
  // When readOnly is true, disable all edit interactions
  const effectiveEnabled = isEnabled && !readOnly;
  const canView = isEnabled;
  // Top-level header states
  const [responsiblePerson, setResponsiblePerson] = useState('');
  const [plannedDate, setPlannedDate] = useState(null);
  const [achievedDate, setAchievedDate] = useState(null);
  const [errors, setErrors] = useState({ person: '', planned: '', achieved: '' });

  // Q1/Q2/Q3/Q4 states (as in your existing component)
  const [rows, setRows] = useState([
    { containment: '', action: '', responsible: '', date: null, status: '' }
  ]);
  const maxContainmentRows = 3;
  const [deliveryStatus, setDeliveryStatus] = useState('Not Delivered');
  const locations = ["Customer Plant", "Warehouse & Transit", "Supplier Plant", "Tier 2"];
  const [data, setData] = useState(
    locations.map((loc) => ({ location: loc, materialSorted: 'Yes', quantity: 0, comments: '' }))
  );
  const [sorting, setSorting] = useState('');

  // Attachments and removals
  const [uploadedFiles, setUploadedFiles] = useState([]);          // [{name, type, base64, ...}]
  const [attachmentsToRemove, setAttachmentsToRemove] = useState([]); // [attachmentId]
  const [containmentDetailToRemove, setContainmentDetailToRemove] = useState([]); // [d3ContainmentId]

  // Optional disable flag
  const [isDisabled, setIsDisabled] = useState(false);

  const initialFormState = useRef(null);
  const [hasChanges, setHasChanges] = useState(false);
  const [formSaved, setFormSaved] = useState(false);

  // Save state & notices
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [saveNotice, setSaveNotice] = useState(null);
  const [d3Id, setD3Id] = useState(null);
  const [isResponsibleSet, setIsResponsibleSet] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [showAchievedDate, setShowAchievedDate] = useState(false);
  const [formClosed, setFormClosed] = useState(true);

  const getFormSnapshot = () => ({
    rows: JSON.stringify(rows),
    deliveryStatus,
    data: JSON.stringify(data),
    sorting,
    uploadedFiles: JSON.stringify(uploadedFiles),
  });

  // -----------------------------
  // Init sorting table rows (skip in edit mode — data comes from API/props)
  // -----------------------------
  useEffect(() => {
    if (d3Data || (eightDInfoId && mode === 'edit')) return;
    setData(
      locations.map((loc) => ({
        location: loc,
        materialSorted: "Yes",
        quantity: 0,
        comments: "",
      }))
    );
  }, []); // eslint-disable-line

  // Load D3 data when available
  useEffect(() => {
    // Fetch from API if eightDInfoId exists and no props data
    if (eightDInfoId && !d3Data && !d3ResponsibleData && mode === 'edit') {
      const fetchD3Data = async () => {
        try {
          const response = await fetchApi('/api/D3/GetD3', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ EightDInfoId: Number(eightDInfoId) }),
          });
          
          if (response?.data) {
            const d3 = response.data;
            if (d3.D3Id) { setD3Id(d3.D3Id); setFormSaved(true); }
            setDeliveryStatus(d3.IsMarkedDeliveredOKParts ? 'Delivered' : 'Not Delivered');
            setSorting(d3.WhatLearnedFromSorting || '');
            if (d3.ResponsiblePerson) setResponsiblePerson(d3.ResponsiblePerson);
            if (d3.PlannedDate) setPlannedDate(new Date(d3.PlannedDate));
            if (d3.AchievedDate) setAchievedDate(new Date(d3.AchievedDate));
            
            if (d3.ContainmentDetails && Array.isArray(d3.ContainmentDetails) && d3.ContainmentDetails.length > 0) {
              setRows(d3.ContainmentDetails.map(cd => ({
                id: cd.D3ContainmentId || cd.d3ContainmentId || cd.Id || cd.id || null,
                containment: cd.Containment || '',
                action: cd.Action || '',
                responsible: cd.Responsible || '',
                date: cd.TargetDate ? new Date(cd.TargetDate) : null,
                status: cd.Status || ''
              })));
            } else {
              setRows([{ containment: '', action: '', responsible: '', date: null, status: '' }]);
            }
            
            if (d3.SortingResults && Array.isArray(d3.SortingResults) && d3.SortingResults.length > 0) {
              setData(d3.SortingResults.map(sr => ({
                id: sr.D3SortingResultId || sr.d3SortingResultId || null,
                location: sr.Location || '',
                materialSorted: sr.IsMaterialSorted ? 'Yes' : 'No',
                quantity: sr.Quantity || 0,
                comments: sr.Comment || ''
              })));
            } else {
              setData(locations.map((loc) => ({ location: loc, materialSorted: 'Yes', quantity: 0, comments: '' })));
            }
            
            if (d3.Attachments && Array.isArray(d3.Attachments)) {
              setUploadedFiles(d3.Attachments);
            }
          }
        } catch (error) {
        }
      };
      fetchD3Data();
      return;
    }
    
    // Reset change tracking whenever source data changes
    setHasChanges(false);
    initialFormState.current = null;

    if (d3Data) {
      const item = Array.isArray(d3Data) ? d3Data[0] : d3Data;
      if (item) {
        if (item.D3Id) { setD3Id(item.D3Id); setFormSaved(true); }
        setDeliveryStatus(item.IsMarkedDeliveredOKParts ? 'Delivered' : 'Not Delivered');
        setSorting(item.WhatLearnedFromSorting || '');
        
        if (item.ContainmentDetails && Array.isArray(item.ContainmentDetails) && item.ContainmentDetails.length > 0) {
          setRows(item.ContainmentDetails.map(cd => ({
            id: cd.D3ContainmentId || cd.d3ContainmentId || cd.Id || cd.id || null,
            containment: cd.Containment || '',
            action: cd.Action || '',
            responsible: cd.Responsible || '',
            date: cd.TargetDate ? new Date(cd.TargetDate) : null,
            status: cd.Status || ''
          })));
        } else {
          setRows([{ containment: '', action: '', responsible: '', date: null, status: '' }]);
        }
        
        if (item.SortingResults && Array.isArray(item.SortingResults) && item.SortingResults.length > 0) {
          setData(item.SortingResults.map(sr => ({
            id: sr.D3SortingResultId || sr.d3SortingResultId || null,
            location: sr.Location || '',
            materialSorted: sr.IsMaterialSorted ? 'Yes' : 'No',
            quantity: sr.Quantity || 0,
            comments: sr.Comment || ''
          })));
        } else {
          setData(locations.map((loc) => ({ location: loc, materialSorted: 'Yes', quantity: 0, comments: '' })));
        }
        
        if (item.Attachments && Array.isArray(item.Attachments)) {
          setUploadedFiles(item.Attachments);
        }
      } else {
        // d3Data is an empty array/falsy item — initialize default rows
        setData(locations.map((loc) => ({ location: loc, materialSorted: 'Yes', quantity: 0, comments: '' })));
      }
    }
    
    if (d3ResponsibleData) {
      const respData = Array.isArray(d3ResponsibleData) ? d3ResponsibleData[0] : d3ResponsibleData;
      if (respData) {
        setResponsiblePerson(respData.ResponsiblePerson || '');
        setPlannedDate(respData.PlannedDate ? new Date(respData.PlannedDate) : null);
        setAchievedDate(respData.AchievedDate ? new Date(respData.AchievedDate) : null);
      }
    }
  }, [eightDInfoId, d3Data, d3ResponsibleData]);

  // Check if responsible person is already set (only when d3Id exists, meaning Set was clicked)
  useEffect(() => {
    if (d3Id) {
      setIsResponsibleSet(true);
    }
  }, [d3Id]);

  // Capture initial snapshot once data is loaded
  useEffect(() => {
    if (initialFormState.current === null) {
      initialFormState.current = getFormSnapshot();
    }
  }); // runs after every render but only sets once

  // Detect changes against initial snapshot
  useEffect(() => {
    if (initialFormState.current === null) return;
    const current = getFormSnapshot();
    const changed = Object.keys(current).some(
      (k) => current[k] !== initialFormState.current[k]
    );
    setHasChanges(changed);
  }, [rows, deliveryStatus, data, sorting, uploadedFiles]); // eslint-disable-line

  // -----------------------------
  // Validation
  // -----------------------------
  const validateField = (field, value) => {
    let error = '';
    if (field === 'person' && !value) error = 'Responsible person is required';
    if (field === 'planned' && !value) error = 'Planned date is required';
    if (field === 'achieved' && !value) error = 'Achieved date is required';
    setErrors((prev) => ({ ...prev, [field]: error }));
  };

  const handlePersonChange = (value) => {
    setResponsiblePerson(value);
    validateField('person', value);
  };
  const handlePlannedChange = (value) => {
    setPlannedDate(value);
    validateField('planned', value);
  };
  const handleAchievedChange = (value) => {
    setAchievedDate(value);
    validateField('achieved', value);
  };

  const isFormValid = () =>
    responsiblePerson && plannedDate && achievedDate &&
    !errors.person && !errors.planned && !errors.achieved;

  // -----------------------------
  // Q1: Containment dynamic rows
  // -----------------------------
  const handleRowChange = (index, field, value) => {
    setRows((prev) =>
      prev.map((row, i) => (i === index ? { ...row, [field]: value } : row))
    );
  };
  const handleAddRow = () => {
    if (rows.length < maxContainmentRows) {
      setRows((prev) => [
        ...prev,
        { containment: '', action: '', responsible: '', date: null, status: '' }
      ]);
    }
  };
  const handleRemoveRow = (index) => {
    const removed = rows[index];
    if (removed?.id) {
      setContainmentDetailToRemove(prev => [...prev, removed.id]);
    }
    setRows((prev) => prev.filter((_, i) => i !== index));
  };

  // -----------------------------
  // Q3: Sorting table handlers
  // -----------------------------
  const handleMaterialSorted = (index, value) => {
    const updated = [...data];
    updated[index].materialSorted = value;
    setData(updated);
  };
  const handleQuantityChange = (index, delta) => {
    const updated = [...data];
    updated[index].quantity = Math.max(0, updated[index].quantity + delta);
    setData(updated);
  };
  const handleCommentChange = (index, value) => {
    const updated = [...data];
    updated[index].comments = value;
    setData(updated);
  };







  const handleUpdateResponsiblePerson = async () => {
    if (!responsiblePerson) return;
    
    const currentEightDInfoId = eightDInfoId || getEightDInfoId();
    setSaveError(null);
    setSaveNotice(null);

    if (!currentEightDInfoId) {
      setSaveError('eightDInfoId is missing.');
      setTimeout(() => setSaveError(null), 2000);
      return;
    }

    setSaving(true);
    try {
      const endpoint = d3Id ? '/api/D3/UpdateD3Responsible' : '/api/D3/CreateD3';
      const response = await fetchApi(endpoint, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          D3Id: d3Id || undefined,
          EightDInfoId: currentEightDInfoId,
          ResponsiblePerson: responsiblePerson,
          PlannedDate: plannedDate?.toISOString(),
          AchievedDate: achievedDate?.toISOString(),
          IsDisabled: true
        }),
      });

      if (response?.data?.D3Id || response?.data?.d3Id) {
        setD3Id(response.data.D3Id || response.data.d3Id);
      }

      setIsResponsibleSet(true);
      setFormClosed(false);
      setSaveNotice(d3Id ? "Updated D3 Form successfully." : "Updated successfully.");
      setTimeout(() => setSaveNotice(null), 3000);
      if (onResponsibleSet) onResponsibleSet();
    } catch (e) {
      setSaveError(e?.message ?? 'Failed to set responsible person.');
      setTimeout(() => setSaveError(null), 2000);
    } finally {
      setSaving(false);
    }
  };

  // Determine if current user is the responsible person for this form
  const isCurrentUserResponsible = responsiblePerson && currentUserEmail && 
    responsiblePerson.toLowerCase() === currentUserEmail.toLowerCase();

  // Form is blocked when:
  // 1. readOnly (user is not originator) AND they are not the responsible person, OR
  // 2. Responsible person is set but is someone else (and user is not originator i.e. readOnly)
  const isFormBlocked = readOnly 
    ? !isCurrentUserResponsible 
    : false;

  const isSaveEnabled = () => {
    if (isFormBlocked) return false;
    // Check Q1: any containment or action filled
    const hasQ1 = rows.some(r => r.containment.trim() || r.action.trim());
    // Check Q2: delivery status selected
    const hasQ2 = !!deliveryStatus;
    // Check Q3: any comment filled or quantity > 0
    const hasQ3 = data.some(r => r.comments.trim() || r.quantity > 0);
    // Check Q4: sorting text filled
    const hasQ4 = !!sorting.trim();
    // Check attachments
    const hasAttachments = uploadedFiles.length > 0;
    return hasQ1 || hasQ2 || hasQ3 || hasQ4 || hasAttachments;
  };

  const handleSave = async () => {
    const currentEightDInfoId = eightDInfoId || getEightDInfoId();
    
    setSaveError(null);
    setSaveNotice(null);

    if (!currentEightDInfoId) {
      setSaveError('EightDInfoId is missing.');
      setTimeout(() => setSaveError(null), 2000);
      return;
    }

    setSaving(true);
    
    try {
      const payload = buildD3Payload({
        eightDInfoId: currentEightDInfoId,
        responsiblePerson,
        plannedDate,
        achievedDate,
        containmentRows: rows,
        sortingResults: data,
        whatLearnedFromSorting: sorting,
        isMarkedDeliveredOKParts: deliveryStatus === 'Delivered',
        uploadedFiles,
        isDisabled,
        containmentToRemove: containmentDetailToRemove,
        attachmentsToRemove,
        createdBy: currentUserEmail,
        modifiedBy: currentUserEmail,
        d3Id,
      });

      const endpoint = d3Id ? '/api/D3/UpdateD3' : '/api/D3/CreateD3';
      



      
      let response;
      try {
        response = await fetchApi(endpoint, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        });
      } catch (fetchError) {
        // API returns 400 but data gets saved — treat as success
        if (fetchError?.message?.includes('400')) {
          console.warn('[D3 Save] API returned 400 but data may be saved, proceeding.');
          response = {};
        } else {
          throw fetchError;
        }
      }
      
      if (response?.data?.d3Id || response?.data?.D3Id) {
        setD3Id(response.data.d3Id || response.data.D3Id);
      }

      // Re-fetch D3 data to get assigned IDs for child records
      try {
        const refreshed = await fetchApi('/api/D3/GetD3', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ EightDInfoId: Number(currentEightDInfoId) }),
        });
        if (refreshed?.data) {
          const d3 = refreshed.data;
          if (d3.ContainmentDetails && Array.isArray(d3.ContainmentDetails) && d3.ContainmentDetails.length > 0) {
            setRows(d3.ContainmentDetails.map(cd => ({
              id: cd.D3ContainmentId || null,
              containment: cd.Containment || '',
              action: cd.Action || '',
              responsible: cd.Responsible || '',
              date: cd.TargetDate ? new Date(cd.TargetDate) : null,
              status: cd.Status || ''
            })));
          }
          if (d3.SortingResults && Array.isArray(d3.SortingResults) && d3.SortingResults.length > 0) {
            setData(d3.SortingResults.map(sr => ({
              id: sr.D3SortingResultId || sr.d3SortingResultId || null,
              location: sr.Location || '',
              materialSorted: sr.IsMaterialSorted ? 'Yes' : 'No',
              quantity: sr.Quantity || 0,
              comments: sr.Comment || ''
            })));
          }
          if (d3.Attachments && Array.isArray(d3.Attachments)) {
            setUploadedFiles(d3.Attachments);
          }
        }
      } catch (e) {
        // Non-critical
      }

      setContainmentDetailToRemove([]);
      setAttachmentsToRemove([]);
      setSaveNotice('D3 containment details saved successfully.');
      setTimeout(() => setSaveNotice(null), 2000);
      setFormSaved(true);
      setSaving(false);
      if (onSaved) onSaved(response);
      return;
    } catch (error) {
      console.error('[D3 Save] Error:', error);
      setSaveError(error?.message || 'Failed to save D3 containment details.');
      setTimeout(() => setSaveError(null), 2000);
      setSaving(false);
    }
  };

  const handleReset = () => {
    // Reset all form fields to initial state
    setRows([{ containment: '', action: '', responsible: '', date: null, status: '' }]);
    setDeliveryStatus('');
    setData(locations.map((loc) => ({
      location: loc,
      materialSorted: "Yes",
      quantity: 0,
      comments: "",
    })));
    setSorting('');
    setUploadedFiles([]);
    
    // Reset change tracking
    setHasChanges(false);
    initialFormState.current = null;
    
    // Clear any error/success messages
    setSaveError(null);
    setSaveNotice(null);
    
    // Show reset confirmation
    setSaveNotice('Form has been reset successfully.');
    setTimeout(() => setSaveNotice(null), 2000);
  };

  const toggleAccordion = () => {
    if (readOnly && isEnabled) {
      onToggleAccordion();
      return;
    }
    if (effectiveEnabled && isEditMode && (d3Id || (responsiblePerson && plannedDate))) {
      onToggleAccordion();
    }
  };

  // -----------------------------
  // Render
  // -----------------------------
  return (
    <>
      <Box className={`d3-header ${canView ? 'enabled' : 'disabled'}`} onClick={toggleAccordion} style={{ cursor: canView ? 'pointer' : 'not-allowed', pointerEvents: canView ? 'auto' : 'none', opacity: canView ? 1 : 0.5 }}>
        {/* Left Section */}
        <Box className="d3-left-section">
          <Box className="d3-circle" style={achievedDate && formSaved ? { backgroundColor: '#4ccb91', borderColor: '#4ccb91' } : (responsiblePerson || isEditMode || formSaved ? { backgroundColor: '#f8c239', borderColor: '#f8c239' } : {})} />
          <span className="d3-title">D3</span>
          <span className="d3-subtitle">Implement and Verify Containment (Temp Fix)</span>

          {/* Tooltip */}
          <Tooltip
            title={
              <span>
                Implement containment to control the situation and mitigate further negative effects of the
                problem. (segregation, selection, temporary actions in the process etc…..)
              </span>
            }
            arrow
            placement="bottom-start"
            componentsProps={{
              tooltip: { className: 'custom-tooltip' },
              arrow: { className: 'custom-arrow' }
            }}
          >
            <IconButton>
              <InfoOutlinedIcon sx={{ color: '#666' }} />
            </IconButton>
          </Tooltip>
        </Box>

        {/* Responsible Person Dropdown */}
        <Box onClick={(e) => e.stopPropagation()}>
          <Select
            value={responsiblePerson}
            onChange={(e) => handlePersonChange(e.target.value)}
            displayEmpty
            variant="standard"
            className="responsible-select"
            error={!!errors.person}
            disabled={!effectiveEnabled || isFormBlocked}
            sx={{
              '& .MuiInput-underline:after': {
                borderBottomColor: '#800040 !important',
              },
              '& .MuiInput-underline:before': {
                borderBottomColor: '#ccc',
              },
              '& .MuiInput-underline:hover:not(.Mui-disabled):before': {
                borderBottomColor: '#800040',
              },
              '& .MuiInputBase-root:after': {
                borderBottomColor: '#800040 !important',
              },
              '& .MuiSelect-root:focus': {
                borderBottomColor: '#800040 !important',
              }
            }}
          >
            <MenuItem value="" sx={{ display: 'none' }}>Responsible Person</MenuItem>
            {teamMembers.map((member) => (
              <MenuItem key={member.D1Id} value={member.EmailId || member.emailId}>
                {member.TeamMemberName || member.teamMemberName}
              </MenuItem>
            ))}
          </Select>
          {errors.person && (
            <Typography className="error-text">{errors.person}</Typography>
          )}
        </Box>

        {/* Planned Date */}
        <Box onClick={(e) => e.stopPropagation()}>
          <TwoMonthDatePicker
            value={plannedDate}
            onChange={(value) => handlePlannedChange(value)}
            label="Planned Date"
            disabled={!effectiveEnabled || isFormBlocked || (isResponsibleSet && !isEditMode)}
            InputLabelProps={{ style: { fontSize: '0.8rem', color: '#777' } }}
          />
        </Box>

        {/* Achieved Date - enabled when responsible person is set */}
        <Box onClick={(e) => {
          e.stopPropagation();
          if (isResponsibleSet && !readOnly && !expanded) {
            setIsEditMode(true);
            setFormClosed(false);
            setHasChanges(false);
            initialFormState.current = null;
            onToggleAccordion();
          }
        }}>
          <TwoMonthDatePicker
            value={achievedDate}
            onChange={(d) => {
              setAchievedDate(d);
              setHasChanges(true);
              if (!isEditMode) {
                setIsEditMode(true);
                onToggleAccordion();
                setTimeout(() => { setHasChanges(false); initialFormState.current = null; }, 0);
              }
            }}
            label="Achieved Date"
            disabled={!isResponsibleSet || isFormBlocked}
            maxDate={new Date()}
            InputLabelProps={{ style: { fontSize: '0.8rem', color: isEditMode && !achievedDate ? '#d32f2f' : '#777' } }}
            sx={isEditMode && !achievedDate ? {
              '& .MuiInput-underline:before': { borderBottomColor: '#d32f2f' },
              '& .MuiInput-underline:after': { borderBottomColor: '#d32f2f' },
              '& .MuiInput-underline:hover:not(.Mui-disabled):before': { borderBottomColor: '#d32f2f' },
              '& .MuiInputLabel-root': { color: '#d32f2f !important' },
            } : {}}
          />
        </Box>

        {/* Edit/Update Buttons */}
        {isResponsibleSet ? (
          <Box className="filBtn float-left" onClick={(e) => e.stopPropagation()} sx={{ display: 'flex', gap: 1 }}>
            <Button 
              variant="outlined" 
              disabled={!effectiveEnabled || isFormBlocked}
              onClick={() => { setIsEditMode(true); setFormClosed(false); onToggleAccordion(); setHasChanges(false); initialFormState.current = null; }}
              sx={{ minWidth: '60px', fontSize: '0.75rem' }}
            >
              Edit
            </Button>
            <Button 
              variant="outlined" 
              disabled={!effectiveEnabled || isFormBlocked || !responsiblePerson || !plannedDate} 
              onClick={handleUpdateResponsiblePerson}
              sx={{ minWidth: '70px', fontSize: '0.75rem' }}
            >
              Update
            </Button>
          </Box>
        ) : (
          <Box className="filBtn float-left" onClick={(e) => e.stopPropagation()}>
            <Button variant="outlined" disabled={!effectiveEnabled || isFormBlocked || !responsiblePerson || !plannedDate} onClick={handleUpdateResponsiblePerson}>Set</Button>
          </Box>
        )}
      </Box>

      {/* Success/Error Messages */}
      {(saveNotice || saveError) && (
        <Box sx={{ position: 'fixed', top: 20, right: 20, zIndex: 9999, minWidth: '300px' }}>
          {saveError && <Alert severity="error" onClose={() => setSaveError(null)}>{saveError}</Alert>}
          {saveNotice && <Alert severity="success" onClose={() => setSaveNotice(null)}>{saveNotice}</Alert>}
        </Box>
      )}

      {/* Accordion Form */}
      {expanded && (isEditMode || readOnly) && (
        <Accordion expanded={expanded} sx={{ mt: 2 }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Box className="accordion-close-button-container">
              <Box
                className="close-button"
                onClick={(e) => {
                  e.stopPropagation();
                  setIsEditMode(false); setFormClosed(true); handleCloseForm();
                }}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  backgroundColor: '#f50057',
                  color: '#fff',
                  padding: '6px 16px',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  '&:hover': { backgroundColor: '#c51162' }
                }}
              >
                <CloseIcon />
                Close Form
              </Box>
            </Box>
          </AccordionSummary>

          <AccordionDetails sx={isFormBlocked ? { pointerEvents: 'none', opacity: 0.6 } : {}}>
            {/* Q1 */}
            <Box className="question-block">
              <Box className="question-label">
                <span className="d2-qnum">Q1</span>
                <span className="d2-question-text">Write containment details?</span>
              </Box>

              {rows.map((row, index) => {
                const hasInput = !!(row.containment?.trim() || row.action?.trim() || row.responsible || row.date);
                const hasAction = !!row.action?.trim();
                const needsContainment = hasInput && !row.containment?.trim();
                const needsResp = hasInput && !row.responsible;
                const needsDate = hasInput && !row.date;
                return (
                <Box key={index} className="q1-container">
                  {/* Fields */}
                  <TextField
                    className="customTextarea"
                    variant="standard"
                    label={needsContainment ? 'Containment *' : 'Containment'}
                    value={row.containment}
                    onChange={(e) => handleRowChange(index, 'containment', e.target.value)}
                    fullWidth
                    multiline
                    disabled={isFormBlocked}
                    error={needsContainment}
                    InputLabelProps={{ style: { fontSize: '0.8rem', color: needsContainment ? '#d32f2f' : '#777' } }}
                    sx={{
                      '& .MuiInput-underline:after': {
                        borderBottomColor: needsContainment ? '#d32f2f' : '#800040',
                      },
                      '& .MuiInput-underline:before': {
                        borderBottomColor: needsContainment ? '#d32f2f' : '#ccc',
                      },
                      '& .MuiInput-underline:hover:not(.Mui-disabled):before': {
                        borderBottomColor: needsContainment ? '#d32f2f' : '#800040',
                      }
                    }}
                  />
                  <TextField
                    className="customTextarea"
                    variant="standard"
                    label="Action"
                    value={row.action}
                    onChange={(e) => handleRowChange(index, 'action', e.target.value)}
                    fullWidth
                    multiline
                    InputLabelProps={{ style: { fontSize: '0.8rem', color: '#777' } }}
                    sx={{
                      '& .MuiInput-underline:after': {
                        borderBottomColor: '#800040',
                      },
                      '& .MuiInput-underline:before': {
                        borderBottomColor: '#ccc',
                      },
                      '& .MuiInput-underline:hover:not(.Mui-disabled):before': {
                        borderBottomColor: '#800040',
                      }
                    }}
                  />
                  <Select
                    value={row.responsible}
                    onChange={(e) => handleRowChange(index, 'responsible', e.target.value)}
                    displayEmpty
                    variant="standard"
                    error={needsResp}
                    sx={{
                      '& .MuiInput-underline:after': {
                        borderBottomColor: needsResp ? '#d32f2f !important' : '#800040 !important',
                      },
                      '& .MuiInput-underline:before': {
                        borderBottomColor: needsResp ? '#d32f2f' : '#ccc',
                      },
                      '& .MuiInput-underline:hover:not(.Mui-disabled):before': {
                        borderBottomColor: needsResp ? '#d32f2f' : '#800040',
                      },
                      '& .MuiInputBase-root:after': {
                        borderBottomColor: needsResp ? '#d32f2f !important' : '#800040 !important',
                      },
                      '& .MuiSelect-select': {
                        color: needsResp ? '#d32f2f' : 'inherit',
                      }
                    }}
                  >
                    <MenuItem value="" sx={{ display: 'none' }}>Responsible for Action</MenuItem>
                    {teamMembers.map((member) => (
                      <MenuItem key={member.D1Id} value={member.TeamMemberName}>
                        {member.TeamMemberName}
                      </MenuItem>
                    ))}
                  </Select> 

                  <TwoMonthDatePicker
                    value={row.date}
                    onChange={(date) => handleRowChange(index, 'date', date)}
                    label={needsDate ? 'Target Date *' : 'Target Date'}
                    error={needsDate}
                    InputLabelProps={{ style: { fontSize: '0.8rem', color: needsDate ? '#d32f2f' : '#777' } }}
                    sx={{
                      '& .MuiInput-underline:before': { borderBottomColor: needsDate ? '#d32f2f' : '#ccc' },
                      '& .MuiInput-underline:after': { borderBottomColor: needsDate ? '#d32f2f' : '#800040' },
                      '& .MuiInput-underline:hover:not(.Mui-disabled):before': { borderBottomColor: needsDate ? '#d32f2f' : '#800040' },
                      '& .MuiInputLabel-root': { color: needsDate ? '#d32f2f !important' : '#777 !important' },
                    }}
                  />

                  <Select
                    value={row.status}
                    onChange={(e) => handleRowChange(index, 'status', e.target.value)}
                    displayEmpty
                    variant="standard"
                    sx={{
                      '& .MuiInput-underline:after': {
                        borderBottomColor: '#800040 !important',
                      },
                      '& .MuiInput-underline:before': {
                        borderBottomColor: '#ccc',
                      },
                      '& .MuiInput-underline:hover:not(.Mui-disabled):before': {
                        borderBottomColor: '#800040',
                      },
                      '& .MuiInputBase-root:after': {
                        borderBottomColor: '#800040 !important',
                      }
                    }}
                  >
                    <MenuItem value="" sx={{ display: 'none' }}>Status</MenuItem>
                    <MenuItem value="Complete">Complete</MenuItem>
                    <MenuItem value="Inprocess">Inprocess</MenuItem>
                  </Select>

                  {/* Conditional Buttons */}
                  {index === rows.length - 1 && rows.length < maxContainmentRows ? (
                    <IconButton onClick={handleAddRow} className="q1-add-btn">
                      <AddIcon />
                    </IconButton>
                  ) : rows.length > 1 ? (
                    <IconButton onClick={() => handleRemoveRow(index)} className="q1-remove-btn">
                      <RemoveIcon />
                    </IconButton>
                  ) : null}
                </Box>
              );})}
            </Box>

            {/* Q2 */}
            <Box className="question-block">
              <Box className="question-label">
                <span className="d2-qnum">Q2</span>
                <span className="d2-question-text">Markings on delivered OK parts?</span>
              </Box>
              <Box className="delivery-options">
                <Button
                  className={`delivery-btn ${deliveryStatus === 'Delivered' ? 'active' : ''}`}
                  onClick={() => setDeliveryStatus('Delivered')}
                >
                  Delivered
                </Button>
                <Button
                  className={`delivery-btn ${deliveryStatus === 'Not Delivered' ? 'active' : ''}`}
                  onClick={() => setDeliveryStatus('Not Delivered')}
                >
                  Not Delivered
                </Button>
              </Box>
            </Box>

            {/* Q3 */}
            <Box className="question-block">
              <Box className="question-label">
                <span className="d2-qnum">Q3</span>
                <span className="d2-question-text">Sorting result at each step of the flow</span>
              </Box>
              <Table className="custom-table">
                <TableHead>
                  <TableRow>
                    <TableCell>Location</TableCell>
                    <TableCell>Material Sorted</TableCell>
                    <TableCell>Quantities</TableCell>
                    <TableCell>Comments</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {data.map((row, index) => (
                    <TableRow key={index}>
                      <TableCell data-label="Location">{row.location}</TableCell>
                      <TableCell data-label="Material Sorted">
                        <div style={{ display: 'flex', gap: '8px' }}>
                          <button
                            className={row.materialSorted === 'Yes' ? 'btn-yes' : 'btn-no'}
                            onClick={() => handleMaterialSorted(index, 'Yes')}
                          >
                            Yes
                          </button>
                          <button
                            className={row.materialSorted === 'No' ? 'btn-yes' : 'btn-no'}
                            onClick={() => handleMaterialSorted(index, 'No')}
                          >
                            No
                          </button>
                        </div>
                      </TableCell>
                      <TableCell data-label="Quantities">
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <button className="icon-btn" onClick={() => handleQuantityChange(index, -1)}>
                            <RemoveIcon />
                          </button>
                          <span>{row.quantity}</span>
                          <button className="icon-btn" onClick={() => handleQuantityChange(index, 1)}>
                            <AddIcon />
                          </button>
                        </div>
                      </TableCell>
                      <TableCell data-label="Comments">
                        <TextField
                          className="customTextarea"
                          variant="standard"
                          value={row.comments}
                          onChange={(e) => handleCommentChange(index, e.target.value)}
                          fullWidth
                          multiline
                          sx={{
                            '& .MuiInput-underline:after': {
                              borderBottomColor: '#800040',
                            },
                            '& .MuiInput-underline:before': {
                              borderBottomColor: '#ccc',
                            },
                            '& .MuiInput-underline:hover:not(.Mui-disabled):before': {
                              borderBottomColor: '#800040',
                            }
                          }}
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>

            {/* Q4 */}
            <Box className="question-block">
              <Box className="question-label">
                <span className="d2-qnum">Q4</span>
                <span className="d2-question-text">What we have learn from the sorting?</span>
              </Box>
              <TextField
                className="customTextarea"
                variant="standard"
                value={sorting}
                onChange={(e) => setSorting(e.target.value)}
                multiline
                sx={{
                  '& .MuiInput-underline:after': {
                    borderBottomColor: '#800040',
                  },
                  '& .MuiInput-underline:before': {
                    borderBottomColor: '#ccc',
                  },
                  '& .MuiInput-underline:hover:not(.Mui-disabled):before': {
                    borderBottomColor: '#800040',
                  }
                }}
              />
            </Box>

            {/* Attachments */}
            <ImageUpload onFilesSelected={(files) => {
              uploadedFiles.forEach(ef => {
                const efId = ef.AttachmentId || ef.attachmentId;
                if (efId && !files.some(f => (f.AttachmentId || f.attachmentId) === efId)) {
                  setAttachmentsToRemove(prev => prev.includes(efId) ? prev : [...prev, efId]);
                }
              });
              setUploadedFiles(files);
            }} folderPath="D3" existingFiles={uploadedFiles} maxFiles={3} rejectedExtensions={['doc', 'docx']} />

            {/* Save and Reset Buttons */}
            <Box className="save-btn-container" sx={{ textAlign: 'center', mt: 3, display: 'flex', gap: 2, justifyContent: 'center' }}>
              <Button 
                variant="contained" 
                className="save-btn" 
                onClick={handleSave}
                disabled={saving || !hasChanges || isFormBlocked}
                sx={{
                  backgroundColor: '#800040',
                  color: '#fff',
                  borderRadius: '20px',
                  textTransform: 'none',
                  px: 4
                }}
              >
                {saving ? 'Saving...' : 'Save'}
              </Button>
              
              <Button 
                variant="outlined" 
                className="reset-btn" 
                onClick={handleReset}
                disabled={saving || isFormBlocked}
                sx={{
                  borderColor: '#800040',
                  color: '#800040',
                  borderRadius: '20px',
                  textTransform: 'none',
                  px: 4,
                  '&:hover': {
                    borderColor: '#660033',
                    backgroundColor: '#f9f3f5'
                  }
                }}
              >
                Reset
              </Button>
            </Box>

            {/* Save notices */}
            {saveError && (
              <Box sx={{ mt: 2 }}>
                <Alert severity="error">{saveError}</Alert>
              </Box>
            )}
            {saveNotice && (
              <Box sx={{ mt: 2 }}>
                <Alert severity="success">{saveNotice}</Alert>
              </Box>
            )}
          </AccordionDetails>
        </Accordion>
      )}
    </>
  );
};

export default D3;


